import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useEmployees } from '@/hooks/use-employees';
import {
  createAbsenceRequest,
  formatDays,
  getAbsenceRequest,
  updateAbsenceRequest,
  getEmployeeAssignedAbsenceTypes,
  type EmployeeAssignedAbsenceType,
  type CreateAbsenceRequestInput,
  type UpdateAbsenceRequestInput,
} from '@/lib/absences';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { usePermission } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addDays, eachDayOfInterval, format, isWeekend, parseISO, startOfDay } from 'date-fns';
import {
  AlertCircle,
  ArrowRight,
  CalendarDays,
  Check,
  ChevronsUpDown,
  HelpCircle,
  Info,
  Loader2,
  Paperclip,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

/** Whose request we are filing — the current user by default, or someone else
 *  when an HR/Lead creates it on their behalf. */
interface RequestTarget {
  id: string;
  firstName: string | null;
  lastName: string | null;
}

interface CreateAbsenceRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialDate?: Date | null;
  /** 'edit' loads an existing request by id and submits an update. */
  mode?: 'create' | 'edit';
  editRequestId?: string | null;
}

export function CreateAbsenceRequestDialog({
  open,
  onOpenChange,
  initialDate,
  mode = 'create',
  editRequestId,
}: CreateAbsenceRequestDialogProps) {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const formatName = useEmployeeNameFormatter();

  // HR Admin / Team Lead may file requests on behalf of others and backdate
  // them. Ordinary users can only request their own present/future time off.
  const canManage = usePermission('absences.requests.manage');
  const canEditPast = usePermission('absences.requests.edit-past');
  const selfEmployee = user?.employee ?? null;

  // The employee the request is for. `null` means "myself" (the default).
  const [targetEmployee, setTargetEmployee] = useState<RequestTarget | null>(null);
  const requestTarget: RequestTarget | null = targetEmployee ?? selfEmployee ?? null;
  const employeeId = requestTarget?.id;
  const isOnBehalf = !!targetEmployee && targetEmployee.id !== selfEmployee?.id;

  // Form state
  const [selectedPolicyId, setSelectedPolicyId] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(initialDate || null);
  const [endDate, setEndDate] = useState<Date | null>(initialDate || null);
  const [halfDayStart, setHalfDayStart] = useState(false);
  const [halfDayEnd, setHalfDayEnd] = useState(false);
  const [isUrgent, setIsUrgent] = useState(false);
  const [notes, setNotes] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [errors, setErrors] = useState<{
    dates?: string;
    policy?: string;
    notes?: string;
    attachments?: string;
    urgent?: string;
    server?: string;
  }>({});

  const isEdit = mode === 'edit' && !!editRequestId;

  // In edit mode, load the full request (the history feed omits half-day flags).
  const { data: editData } = useQuery({
    queryKey: ['absence-request', editRequestId],
    queryFn: () => getAbsenceRequest(editRequestId!),
    enabled: open && isEdit,
  });

  // Reset form when opening in create mode. Edit mode prefills from `editData`.
  useEffect(() => {
    if (!open || isEdit) return;
    setSelectedPolicyId(null);
    setTargetEmployee(null);
    setStartDate(initialDate || null);
    setEndDate(initialDate || null);
    setHalfDayStart(false);
    setHalfDayEnd(false);
    setIsUrgent(false);
    setNotes('');
    setAttachments([]);
    setErrors({});
  }, [open, initialDate, isEdit]);

  // Prefill from the loaded request when editing. While it loads (or if the
  // fetch fails) the form is cleared, so a previously-edited record's values
  // never linger under the new id — which could otherwise be saved against the
  // wrong request. Keeps a single mounted instance (preserves close animation).
  useEffect(() => {
    if (!open || !isEdit) return;
    if (!editData) {
      setTargetEmployee(null);
      setSelectedPolicyId(null);
      setStartDate(null);
      setEndDate(null);
      setHalfDayStart(false);
      setHalfDayEnd(false);
      setIsUrgent(false);
      setNotes('');
      setAttachments([]);
      setErrors({});
      return;
    }
    setTargetEmployee({
      id: editData.employeeId,
      firstName: editData.employee.firstName,
      lastName: editData.employee.lastName,
    });
    setSelectedPolicyId(editData.absenceTypeId);
    setStartDate(parseISO(editData.startDate.slice(0, 10)));
    setEndDate(parseISO(editData.endDate.slice(0, 10)));
    setHalfDayStart(editData.halfDayStart);
    setHalfDayEnd(editData.halfDayEnd);
    setIsUrgent(false);
    setNotes(editData.notes ?? '');
    setAttachments([]);
    setErrors({});
  }, [open, isEdit, editData]);

  // Balances are validated by the backend against the START-date's calendar year
  // (request-validation.service.ts), not "today". Drive the displayed balance and
  // the client-side gate off that same year so the dialog can't promise days the
  // request will then be rejected for (e.g. a Dec→Jan or backdated request). [HW-687]
  const balanceYear = (startDate ?? new Date()).getFullYear();

  // Fetch assigned absence types for the current employee, scoped to the
  // start-date's balance year.
  const { data: absenceTypesData, isLoading: absenceTypesLoading } = useQuery({
    queryKey: ['employee-assigned-absence-types', employeeId, balanceYear],
    queryFn: () => getEmployeeAssignedAbsenceTypes(employeeId!, balanceYear),
    enabled: !!employeeId && open,
    placeholderData: keepPreviousData,
  });

  // HW-718: scope to the current employee — the query is disabled when none is
  // selected, but keepPreviousData would otherwise keep serving the prior
  // employee's types (stale balances under a blank Employee field on reopen).
  const assignedAbsenceTypes = employeeId ? (absenceTypesData?.data ?? []) : [];

  // Default request rules (fallback for backward compatibility)
  const defaultRequestRules = {
    minNoticeDays: 0,
    maxAdvanceDays: null as number | null,
    minRequestAmount: 0.5,
    maxRequestAmount: null as number | null,
    allowNegativeBalance: false,
    allowAdvanceAgainstAccrual: false,
    preventOverlaps: true,
    allowedDuringProbation: 'allowed' as const,
    probationLimit: null as number | null,
    requesterCommentRequired: false,
    instructions: null as string | null,
    enableOnDemand: false,
    onDemandAmount: 0,
  };

  // Filter to only show absence types where employee can submit
  const submittableAbsenceTypes = useMemo(() => {
    return assignedAbsenceTypes.filter((p) => p.absenceType.employeeCanSubmit);
  }, [assignedAbsenceTypes]);

  // Creating is limited to employee-submittable types; editing may target any
  // assigned type (including HR-only ones) so the record's own type resolves.
  const selectableAbsenceTypes = useMemo(
    () => (isEdit ? assignedAbsenceTypes : submittableAbsenceTypes),
    [isEdit, assignedAbsenceTypes, submittableAbsenceTypes]
  );

  // Get request rules with fallback to defaults
  const getRequestRules = (assigned: EmployeeAssignedAbsenceType | undefined) => {
    return assigned?.absenceType.requestRules ?? defaultRequestRules;
  };

  // Auto-select first absence type if only one is available
  useEffect(() => {
    if (selectableAbsenceTypes.length === 1 && !selectedPolicyId) {
      setSelectedPolicyId(selectableAbsenceTypes[0].absenceType.id);
    }
  }, [selectableAbsenceTypes, selectedPolicyId]);

  // Get selected absence type data
  const selectedAbsenceType = useMemo(() => {
    return selectableAbsenceTypes.find((p) => p.absenceType.id === selectedPolicyId);
  }, [selectableAbsenceTypes, selectedPolicyId]);

  // Get request rules for selected absence type (with defaults)
  const requestRules = useMemo(() => {
    return getRequestRules(selectedAbsenceType);
  }, [selectedAbsenceType]);

  // Date constraints: `edit-past` holders and the edit dialog may pick past
  // dates; `manage`/`edit-past` holders bypass the advance-booking ceiling.
  const dateConstraints = useMemo<{ minDate?: Date; maxDate?: Date }>(() => {
    const today = startOfDay(new Date());
    const unlockPast = canEditPast || isEdit;
    const unlockFuture = canManage || canEditPast || isEdit;
    return {
      minDate: unlockPast
        ? undefined
        : requestRules.minNoticeDays
          ? addDays(today, requestRules.minNoticeDays)
          : today,
      maxDate: unlockFuture
        ? undefined
        : requestRules.maxAdvanceDays
          ? addDays(today, requestRules.maxAdvanceDays)
          : undefined,
    };
  }, [requestRules, canManage, canEditPast, isEdit]);

  // Calculate total days between dates (respects countingMethod and half days)
  const totalDays = useMemo(() => {
    if (!startDate || !endDate || !selectedAbsenceType) return 0;
    if (endDate < startDate) return 0;

    const days = eachDayOfInterval({ start: startDate, end: endDate });
    
    // Count based on counting method
    let count: number;
    if (selectedAbsenceType.absenceType.countingMethod === 'calendar_days') {
      count = days.length;
    } else {
      // working_days - exclude weekends
      count = days.filter((day) => !isWeekend(day)).length;
    }

    // Apply half day adjustments
    if (halfDayStart && count > 0) count -= 0.5;
    if (halfDayEnd && count > 0) count -= 0.5;

    return Math.max(0, count);
  }, [startDate, endDate, selectedAbsenceType, halfDayStart, halfDayEnd]);

  // When editing in place, the record's own days are already in the balance.
  // Credit them back for a same-type edit so the check mirrors the backend.
  const creditBackDays = useMemo(() => {
    if (!isEdit || !editData || editData.absenceTypeId !== selectedPolicyId) return 0;
    return editData.totalDays;
  }, [isEdit, editData, selectedPolicyId]);

  // Calculate remaining balance after request
  const remainingBalance = useMemo(() => {
    if (!selectedAbsenceType || selectedAbsenceType.absenceType.unlimitedBalance) return null;
    const available = (selectedAbsenceType.balance.available ?? 0) + creditBackDays;
    return available - totalDays;
  }, [selectedAbsenceType, totalDays, creditBackDays]);

  // Check if request exceeds balance
  const exceedsBalance = useMemo(() => {
    if (!selectedAbsenceType || selectedAbsenceType.absenceType.unlimitedBalance) return false;
    const available = (selectedAbsenceType.balance.available ?? 0) + creditBackDays;
    return totalDays > available;
  }, [selectedAbsenceType, totalDays, creditBackDays]);

  // Mirrors the backend's local-midnight "is past" check: backdated requests
  // save straight to APPROVED, so the approval copy must reflect that.
  const isBackdated = useMemo(() => {
    if (!startDate) return false;
    return startOfDay(startDate).getTime() < startOfDay(new Date()).getTime();
  }, [startDate]);

  // Check if urgent (without approval) option is available
  const canUseUrgent = useMemo(() => {
    if (!requestRules.enableOnDemand) return false;
    if (totalDays === 0) return false;
    return totalDays <= requestRules.onDemandAmount;
  }, [requestRules.enableOnDemand, requestRules.onDemandAmount, totalDays]);

  // Auto-uncheck urgent if no longer valid
  useEffect(() => {
    if (isUrgent && !canUseUrgent) {
      setIsUrgent(false);
    }
  }, [isUrgent, canUseUrgent]);

  const submitMutation = useMutation({
    mutationFn: (vars: { employeeId: string; input: CreateAbsenceRequestInput | UpdateAbsenceRequestInput }) =>
      isEdit
        ? updateAbsenceRequest(editRequestId!, vars.input as UpdateAbsenceRequestInput)
        : createAbsenceRequest(vars.input as CreateAbsenceRequestInput),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['absence-requests'] });
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      queryClient.invalidateQueries({ queryKey: ['employee-assigned-absence-types', variables.employeeId] });
      queryClient.invalidateQueries({ queryKey: ['employee-policies', variables.employeeId] });
      queryClient.invalidateQueries({ queryKey: ['employee-absences', variables.employeeId] });
      // Refresh the detail that prefills this dialog, so reopening the same
      // record shows the saved values instead of a stale (cached) prefill.
      if (editRequestId) {
        queryClient.invalidateQueries({ queryKey: ['absence-request', editRequestId] });
      }
      setConfirmOpen(false);
      onOpenChange(false);
    },
    onError: (error: unknown) => {
      let message = isEdit ? 'Failed to update absence request' : 'Failed to create absence request';

      if (error && typeof error === 'object' && 'response' in error) {
        const res = (error as { response?: { data?: { errors?: { message: string }[]; message?: string } } }).response;
        const data = res?.data;
        if (data?.errors?.length) {
          message = data.errors.map((e) => e.message).join('. ');
        } else if (data?.message) {
          message = data.message;
        }
      } else if (error instanceof Error && error.message) {
        message = error.message;
      }

      // Keep the confirmation modal open and surface the error inside it, rather
      // than dropping back to a generic banner on the form behind it. [HW-687]
      setErrors({ server: message });
    },
  });

  // Submit goes through an explicit confirmation step (see confirmSubmit).
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleSubmit = () => {
    const newErrors: typeof errors = {};

    if (!selectedPolicyId) {
      newErrors.policy = 'Please select an absence type';
    }

    if (!startDate || !endDate) {
      newErrors.dates = 'Please select both start and end dates';
    } else if (endDate < startDate) {
      newErrors.dates = 'End date must be after start date';
    } else if (totalDays === 0) {
      newErrors.dates = 'Selected range contains no working days';
    } else {
      // Check min request amount
      if (requestRules.minRequestAmount && totalDays < requestRules.minRequestAmount) {
        newErrors.dates = `Minimum request is ${requestRules.minRequestAmount} days`;
      }
      // Check max request amount
      if (requestRules.maxRequestAmount && totalDays > requestRules.maxRequestAmount) {
        newErrors.dates = `Maximum request is ${requestRules.maxRequestAmount} days per request`;
      }
    }

    // Check required comment
    if (requestRules.requesterCommentRequired && !notes.trim()) {
      newErrors.notes = 'Comment is required for this absence type';
    }

    // Check required attachments. Skip in edit mode — editing doesn't re-handle
    // attachments (the update payload omits them), so requiring one would make
    // records of an attachment-required type uneditable.
    if (!isEdit && selectedAbsenceType?.absenceType.attachmentMode === 'required' && attachments.length === 0) {
      newErrors.attachments = 'Attachment is required for this absence type';
    }

    // Check balance (block if not allowed negative). Name the year explicitly:
    // the balance shown + checked is the start-date year's, which is what the
    // backend validates against — so this message matches the eventual reject
    // instead of contradicting it after submit. [HW-687]
    // [HW-689] When the type allows booking against an imminent accrual, don't
    // block over-balance here — let the backend accept it for HR review if the
    // accrual covers it before the start date, or reject with a precise message
    // (surfaced in the confirmation modal, per HW-687).
    if (
      exceedsBalance &&
      !requestRules.allowNegativeBalance &&
      !requestRules.allowAdvanceAgainstAccrual
    ) {
      const available = (selectedAbsenceType?.balance.available ?? 0) + creditBackDays;
      newErrors.dates = `Insufficient ${balanceYear} balance for ${
        selectedAbsenceType?.absenceType.name ?? 'this type'
      }: ${formatDays(available)} day(s) available, ${formatDays(totalDays)} requested.`;
    }

    // Check urgent/on-demand limits
    if (isUrgent) {
      if (!requestRules.enableOnDemand) {
        newErrors.urgent = 'Urgent requests are not enabled for this absence type';
      } else if (totalDays > requestRules.onDemandAmount) {
        newErrors.urgent = `Urgent requests are limited to ${requestRules.onDemandAmount} days maximum`;
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (!employeeId || !selectedPolicyId || !startDate || !endDate) return;

    // Validation passed — open the confirmation step so the requester
    // explicitly sees the day count and balance impact before it's submitted.
    setErrors({});
    setConfirmOpen(true);
  };

  const confirmSubmit = () => {
    if (!employeeId || !selectedPolicyId || !startDate || !endDate) return;

    if (isEdit) {
      const startStr = format(startDate, 'yyyy-MM-dd');
      const endStr = format(endDate, 'yyyy-MM-dd');
      // Resend the day count only when something that affects it actually
      // changed vs the loaded record. Otherwise a notes-only edit could restate
      // an APPROVED record's balance via a recomputed totalDays that drifts from
      // the stored value; when omitted the backend keeps existing.totalDays.
      const dayCountAffected =
        selectedPolicyId !== editData?.absenceTypeId ||
        startStr !== editData?.startDate.slice(0, 10) ||
        endStr !== editData?.endDate.slice(0, 10) ||
        halfDayStart !== editData?.halfDayStart ||
        halfDayEnd !== editData?.halfDayEnd;
      submitMutation.mutate({
        employeeId,
        input: {
          absenceTypeId: selectedPolicyId,
          startDate: startStr,
          endDate: endStr,
          halfDayStart,
          halfDayEnd,
          ...(dayCountAffected ? { totalDays } : {}),
          // Editing must be able to clear a note: send the literal (possibly
          // empty) value; collapsing '' to undefined makes the backend keep the
          // old note.
          notes: notes.trim(),
        },
      });
      return;
    }

    submitMutation.mutate({
      employeeId,
      input: {
        employeeId,
        absenceTypeId: selectedPolicyId,
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
        totalDays,
        halfDayStart,
        halfDayEnd,
        isUrgent,
        notes: notes.trim() || undefined,
      },
    });
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-[50%] top-[50%] z-50 w-full max-w-[520px] max-h-[90vh] translate-x-[-50%] translate-y-[-50%]',
            'border bg-card shadow-lg rounded-xl overflow-hidden',
            'flex flex-col',
            'duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
            'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]'
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b shrink-0">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                <CalendarDays className="h-5 w-5 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-base font-semibold">
                  {isEdit ? 'Edit Absence Request' : 'New Absence Request'}
                  {requestTarget && (
                    <span className="font-normal text-muted-foreground text-sm ml-1.5">
                      ({formatName({ firstName: requestTarget.firstName ?? '', lastName: requestTarget.lastName ?? '' })})
                    </span>
                  )}
                </DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground mt-0.5">
                  {isOnBehalf
                    ? 'Filing this request on behalf of the selected employee'
                    : 'Select type and dates for your time off'}
                </DialogDescription>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-full text-muted-foreground hover:text-foreground"
                    >
                      <HelpCircle className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs">
                    <p className="text-xs">
                      View absence policies and rules for your organization.
                      <br />
                      {/* Dimmed secondary line — `text-muted-foreground` is calibrated
                          for light surfaces and disappears against the tooltip's
                          dark `bg-foreground` background. */}
                      <span className="text-background/75">Policy details coming soon...</span>
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <DialogPrimitive.Close asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </Button>
              </DialogPrimitive.Close>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-5 overflow-y-auto flex-1 min-h-0">
            {/* Error Alert */}
            {errors.server && (
              <div className="rounded-lg bg-destructive/10 px-3 py-2.5 text-sm text-destructive flex items-center gap-2">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {errors.server}
              </div>
            )}

            {/* On-behalf-of selector — HR/Lead can file for another employee */}
            {canManage && !isEdit && (
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Employee
                </label>
                <RequestTargetSelect
                  self={selfEmployee}
                  value={requestTarget}
                  disabled={submitMutation.isPending}
                  onChange={(employee) => {
                    // Switching employee invalidates the type/dates picked for
                    // the previous one — start their request from scratch.
                    setTargetEmployee(
                      employee && employee.id !== selfEmployee?.id ? employee : null,
                    );
                    setSelectedPolicyId(null);
                    setStartDate(null);
                    setEndDate(null);
                    setErrors({});
                  }}
                />
              </div>
            )}

            {/* Absence Type Selector */}
            <div className="space-y-3">
              {absenceTypesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : selectableAbsenceTypes.length === 0 ? (
                <div className="rounded-xl border border-dashed p-6 text-center">
                  <Info className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm font-medium">No absence types available</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Contact HR to get absence types assigned to your profile.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {selectableAbsenceTypes.map((item) => (
                    <AbsenceTypeOption
                      key={item.absenceType.id}
                      item={item}
                      selected={selectedPolicyId === item.absenceType.id}
                      onSelect={() => {
                        setSelectedPolicyId(item.absenceType.id);
                        if (errors.policy) setErrors((e) => ({ ...e, policy: undefined }));
                      }}
                      disabled={submitMutation.isPending}
                    />
                  ))}
                </div>
              )}
              {errors.policy && <p className="text-xs text-destructive">{errors.policy}</p>}
            </div>

            {/* Instructions */}
            {requestRules.instructions && (
              <div className="rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/50 px-3 py-2.5">
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 shrink-0 mt-0.5 text-blue-600 dark:text-blue-400" />
                  <p className="text-xs text-blue-800 dark:text-blue-200">
                    {requestRules.instructions}
                  </p>
                </div>
              </div>
            )}

            {/* Date Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Start Date
                </label>
                <DatePicker
                  value={startDate}
                  onChange={(date) => {
                    setStartDate(date);
                    if (errors.dates) setErrors((e) => ({ ...e, dates: undefined }));
                  }}
                  placeholder="Select date"
                  disabled={submitMutation.isPending || !selectedPolicyId}
                  minDate={dateConstraints.minDate}
                  maxDate={dateConstraints.maxDate}
                />
                {!canManage && !canEditPast && requestRules.minNoticeDays > 0 && (
                  <p className="text-[10px] text-muted-foreground">
                    Min {requestRules.minNoticeDays} days notice required
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  End Date
                </label>
                <DatePicker
                  value={endDate}
                  onChange={(date) => {
                    setEndDate(date);
                    if (errors.dates) setErrors((e) => ({ ...e, dates: undefined }));
                  }}
                  placeholder="Select date"
                  disabled={submitMutation.isPending || !selectedPolicyId}
                  minDate={startDate || dateConstraints.minDate}
                  maxDate={dateConstraints.maxDate}
                />
                {!canManage && !canEditPast && requestRules.maxAdvanceDays && (
                  <p className="text-[10px] text-muted-foreground">
                    Max {requestRules.maxAdvanceDays} days in advance
                  </p>
                )}
              </div>
            </div>
            {errors.dates && <p className="text-xs text-destructive -mt-3">{errors.dates}</p>}

            {/* Half Days */}
            {selectedAbsenceType?.absenceType.allowHalfDays && startDate && endDate && (
              <div className="flex flex-wrap items-center gap-4 rounded-lg bg-muted/50 px-4 py-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="halfDayStart"
                    checked={halfDayStart}
                    onCheckedChange={(checked) => setHalfDayStart(!!checked)}
                    disabled={submitMutation.isPending}
                  />
                  <label htmlFor="halfDayStart" className="text-sm cursor-pointer">
                    Half day at start (PM only)
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="halfDayEnd"
                    checked={halfDayEnd}
                    onCheckedChange={(checked) => setHalfDayEnd(!!checked)}
                    disabled={submitMutation.isPending}
                  />
                  <label htmlFor="halfDayEnd" className="text-sm cursor-pointer">
                    Half day at end (AM only)
                  </label>
                </div>
              </div>
            )}

            {/* Urgent (Without Approval) */}
            {!isEdit && requestRules.enableOnDemand && startDate && endDate && totalDays > 0 && (
              <div
                className={cn(
                  'rounded-lg px-4 py-3',
                  canUseUrgent
                    ? 'bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50'
                    : 'bg-muted/50 border border-border'
                )}
              >
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="isUrgent"
                    checked={isUrgent}
                    onCheckedChange={(checked) => {
                      setIsUrgent(!!checked);
                      if (errors.urgent) setErrors((e) => ({ ...e, urgent: undefined }));
                    }}
                    disabled={submitMutation.isPending || !canUseUrgent}
                  />
                  <div className="flex-1">
                    <label
                      htmlFor="isUrgent"
                      className={cn(
                        'text-sm font-medium cursor-pointer',
                        canUseUrgent ? 'text-amber-800 dark:text-amber-200' : 'text-muted-foreground'
                      )}
                    >
                      Urgent (without approval)
                    </label>
                    <p
                      className={cn(
                        'text-xs mt-0.5',
                        canUseUrgent
                          ? 'text-amber-700 dark:text-amber-300'
                          : 'text-muted-foreground'
                      )}
                    >
                      {canUseUrgent
                        ? `Available for requests up to ${requestRules.onDemandAmount} days (no approval needed)`
                        : `Not available: exceeds ${requestRules.onDemandAmount} day limit`}
                    </p>
                  </div>
                </div>
                {errors.urgent && (
                  <p className="text-xs text-destructive mt-2 ml-7">{errors.urgent}</p>
                )}
              </div>
            )}

            {/* Comment */}
            <div className="space-y-1.5">
              <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Comment{' '}
                {requestRules.requesterCommentRequired ? (
                  <span className="text-destructive">*</span>
                ) : (
                  <span className="font-normal normal-case">(optional)</span>
                )}
              </label>
              <textarea
                value={notes}
                onChange={(e) => {
                  setNotes(e.target.value);
                  if (errors.notes) setErrors((e) => ({ ...e, notes: undefined }));
                }}
                placeholder="Add any additional context..."
                disabled={submitMutation.isPending}
                className={cn(
                  'flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm',
                  'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                  'disabled:cursor-not-allowed disabled:opacity-50 resize-none',
                  errors.notes && 'border-destructive'
                )}
              />
              {errors.notes && <p className="text-xs text-destructive">{errors.notes}</p>}
            </div>

            {/* Attachments — create only; the update payload doesn't carry them. */}
            {!isEdit && selectedAbsenceType?.absenceType.attachmentMode !== 'disabled' && (
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Attachments{' '}
                  {selectedAbsenceType?.absenceType.attachmentMode === 'required' ? (
                    <span className="text-destructive">*</span>
                  ) : (
                    <span className="font-normal normal-case">(optional)</span>
                  )}
                </label>
                <div
                  className={cn(
                    'flex flex-col gap-2 rounded-lg border border-dashed p-4',
                    errors.attachments && 'border-destructive'
                  )}
                >
                  {attachments.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {attachments.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 rounded-md bg-muted px-2 py-1 text-sm"
                        >
                          <Paperclip className="h-3 w-3" />
                          <span className="max-w-[150px] truncate">{file.name}</span>
                          <button
                            type="button"
                            onClick={() =>
                              setAttachments((prev) => prev.filter((_, i) => i !== index))
                            }
                            className="text-muted-foreground hover:text-foreground"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <label className="flex cursor-pointer items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                    <Paperclip className="h-4 w-4" />
                    <span>Click to upload or drag and drop</span>
                    <input
                      type="file"
                      className="hidden"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        setAttachments((prev) => [...prev, ...files].slice(0, 3));
                        if (errors.attachments) setErrors((e) => ({ ...e, attachments: undefined }));
                        e.target.value = '';
                      }}
                      disabled={submitMutation.isPending || attachments.length >= 3}
                    />
                  </label>
                  <p className="text-[10px] text-muted-foreground text-center">
                    PDF, JPG, PNG, DOC up to 5MB (max 3 files)
                  </p>
                </div>
                {errors.attachments && (
                  <p className="text-xs text-destructive">{errors.attachments}</p>
                )}
              </div>
            )}

            {/* Summary Strip */}
            {selectedAbsenceType && startDate && endDate && totalDays > 0 && (
              <div className="flex items-stretch rounded-xl bg-muted/50 overflow-hidden">
                <div className="flex-1 px-4 py-3 text-center">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                    Duration
                  </div>
                  <div className="text-lg font-bold">{totalDays} days</div>
                  {(requestRules.minRequestAmount > 0 || requestRules.maxRequestAmount) && (
                    <div className="text-[10px] text-muted-foreground">
                      {requestRules.minRequestAmount > 0 &&
                        `min ${requestRules.minRequestAmount}`}
                      {requestRules.minRequestAmount > 0 &&
                        requestRules.maxRequestAmount &&
                        ' / '}
                      {requestRules.maxRequestAmount &&
                        `max ${requestRules.maxRequestAmount}`}
                    </div>
                  )}
                </div>
                <div className="flex-1 px-4 py-3 text-center border-l border-border">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                    Current
                  </div>
                  <div className="text-lg font-bold">
                    {selectedAbsenceType.absenceType.unlimitedBalance
                      ? '∞'
                      : formatDays(selectedAbsenceType.balance.available ?? 0)}
                  </div>
                </div>
                <div className="flex items-center justify-center px-3 bg-muted">
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex-1 px-4 py-3 text-center border-l border-border">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                    Remaining
                  </div>
                  <div
                    className={cn(
                      'text-lg font-bold',
                      selectedAbsenceType.absenceType.unlimitedBalance
                        ? 'text-foreground'
                        : exceedsBalance
                          ? 'text-destructive'
                          : 'text-emerald-600 dark:text-emerald-500'
                    )}
                  >
                    {selectedAbsenceType.absenceType.unlimitedBalance ? '∞' : formatDays(remainingBalance ?? 0)}
                  </div>
                </div>
              </div>
            )}

            {/* Warning for exceeding balance */}
            {exceedsBalance && (
              <div
                className={cn(
                  'rounded-lg px-3 py-2.5 text-sm flex items-center gap-2',
                  requestRules.allowNegativeBalance || requestRules.allowAdvanceAgainstAccrual
                    ? 'bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 text-amber-800 dark:text-amber-200'
                    : 'bg-destructive/10 border border-destructive/20 text-destructive'
                )}
              >
                <AlertCircle className="h-4 w-4 shrink-0" />
                {requestRules.allowNegativeBalance
                  ? 'Request exceeds your available balance (negative balance allowed)'
                  : requestRules.allowAdvanceAgainstAccrual
                    ? 'Over your current balance — if an upcoming accrual covers it before the start date, this is sent to HR for review instead of being blocked.'
                    : 'Request exceeds your available balance'}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t bg-muted/30 shrink-0">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Info className="h-3.5 w-3.5" />
              {isEdit
                ? 'Changes will be saved — balance recalculated automatically'
                : isUrgent
                  ? 'Urgent request will be auto-approved (no approval needed)'
                  : isBackdated
                    ? 'Retroactive record — saved immediately as approved'
                    : selectedAbsenceType?.absenceType.approvalRequired
                      ? 'Request will be sent to your manager for approval'
                      : 'Request will be auto-approved'}
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={submitMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={
                  submitMutation.isPending ||
                  !selectedPolicyId ||
                  !startDate ||
                  !endDate ||
                  totalDays === 0 ||
                  (exceedsBalance &&
                    !requestRules.allowNegativeBalance &&
                    !requestRules.allowAdvanceAgainstAccrual)
                }
              >
                {submitMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Submit
                {!submitMutation.isPending && <Check className="ml-2 h-4 w-4" />}
              </Button>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>

    {/* Explicit confirmation — surfaces the day count + balance impact so the
        requester sees exactly how many days are being taken before submitting. */}
    <AlertDialog
      open={confirmOpen}
      onOpenChange={(o) => {
        if (submitMutation.isPending) return;
        setConfirmOpen(o);
        // Going back to the form clears a prior submit error so it doesn't
        // linger behind the dialog. [HW-687]
        if (!o) setErrors((prev) => ({ ...prev, server: undefined }));
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm your time off</AlertDialogTitle>
          <AlertDialogDescription>
            Please review the details below — this is exactly what will be requested.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {selectedAbsenceType && startDate && endDate && (
          <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm space-y-2.5">
            <div className="text-foreground">
              You're requesting{' '}
              <span className="font-semibold">
                {totalDays} {totalDays === 1 ? 'day' : 'days'}
              </span>{' '}
              of <span className="font-semibold">{selectedAbsenceType.absenceType.name}</span>.
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <CalendarDays className="h-4 w-4 shrink-0" />
              <span>
                {format(startDate, 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd')
                  ? format(startDate, 'EEEE, MMMM d, yyyy')
                  : `${format(startDate, 'EEE, MMM d')} → ${format(endDate, 'EEE, MMM d, yyyy')}`}
              </span>
            </div>
            {!selectedAbsenceType.absenceType.unlimitedBalance && remainingBalance !== null && (
              <div className="text-muted-foreground">
                {selectedAbsenceType.absenceType.name} balance:{' '}
                <span className="font-medium text-foreground">
                  {formatDays(selectedAbsenceType.balance.available ?? 0)}
                </span>{' '}
                →{' '}
                <span
                  className={cn(
                    'font-semibold',
                    remainingBalance < 0 ? 'text-destructive' : 'text-foreground'
                  )}
                >
                  {formatDays(remainingBalance)}
                </span>{' '}
                days left
              </div>
            )}
            <div className="pt-1 text-xs text-muted-foreground">
              {isEdit
                ? 'Changes will be saved and the balance recalculated automatically.'
                : isUrgent
                  ? 'Urgent — applied immediately, no approval needed.'
                  : isBackdated
                    ? 'Retroactive record — applied immediately as approved.'
                    : selectedAbsenceType?.absenceType.approvalRequired
                      ? 'This request will be sent to your manager for approval.'
                      : 'This request will be auto-approved.'}
            </div>
          </div>
        )}

        {errors.server && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {errors.server}
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={submitMutation.isPending}>Back</AlertDialogCancel>
          <AlertDialogAction
            variant="default"
            onClick={(e) => {
              e.preventDefault();
              confirmSubmit();
            }}
            disabled={submitMutation.isPending}
          >
            {submitMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Confirm &amp; submit
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

// On-behalf-of employee picker. Server-side search; "Myself" resets the
// request back to the current user.
function RequestTargetSelect({
  self,
  value,
  disabled,
  onChange,
}: {
  self: RequestTarget | null;
  value: RequestTarget | null;
  disabled?: boolean;
  onChange: (employee: RequestTarget | null) => void;
}) {
  const formatName = useEmployeeNameFormatter();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { data, isLoading } = useEmployees({
    search: search.trim() || undefined,
    status: 'active',
    limit: 20,
  });
  const employees = data?.data ?? [];

  const isSelf = !!value && !!self && value.id === self.id;
  const label = value
    ? formatName({ firstName: value.firstName ?? '', lastName: value.lastName ?? '' }) || 'Unnamed employee'
    : 'Select employee';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span className="truncate">
            {label}
            {isSelf && <span className="text-muted-foreground"> (you)</span>}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search employees..."
            value={search}
            onValueChange={setSearch}
            className="h-9"
          />
          <CommandList>
            {isLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <CommandEmpty>No employees found</CommandEmpty>
                {self && (
                  <CommandGroup>
                    <CommandItem
                      value="__self__"
                      onSelect={() => {
                        onChange(null);
                        setOpen(false);
                      }}
                    >
                      <Check className={cn('mr-2 h-4 w-4', isSelf ? 'opacity-100' : 'opacity-0')} />
                      Myself ({formatName({ firstName: self.firstName ?? '', lastName: self.lastName ?? '' })})
                    </CommandItem>
                  </CommandGroup>
                )}
                <CommandGroup>
                  {employees
                    .filter((e) => e.id !== self?.id)
                    .map((employee) => {
                      const selected = value?.id === employee.id;
                      return (
                        <CommandItem
                          key={employee.id}
                          value={employee.id}
                          onSelect={() => {
                            onChange({
                              id: employee.id,
                              firstName: employee.firstName ?? null,
                              lastName: employee.lastName ?? null,
                            });
                            setOpen(false);
                          }}
                        >
                          <Check
                            className={cn('mr-2 h-4 w-4', selected ? 'opacity-100' : 'opacity-0')}
                          />
                          <span className="truncate">
                            {formatName({ firstName: employee.firstName ?? '', lastName: employee.lastName ?? '' }) ||
                              employee.workEmail}
                          </span>
                        </CommandItem>
                      );
                    })}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Absence type option component
function AbsenceTypeOption({
  item,
  selected,
  onSelect,
  disabled,
}: {
  item: EmployeeAssignedAbsenceType;
  selected: boolean;
  onSelect: () => void;
  disabled: boolean;
}) {
  const absenceType = item.absenceType;
  const isUnlimited = absenceType.unlimitedBalance;
  const color = absenceType.color || '#6B7280';

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      className={cn(
        'flex flex-col items-center gap-1.5 rounded-lg p-2.5 text-center transition-all',
        'border-2',
        selected ? 'border-primary bg-primary/5' : 'border-transparent bg-muted/50 hover:bg-muted',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      {/* Radio indicator */}
      <div
        className={cn(
          'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-all',
          selected ? 'border-primary' : 'border-muted-foreground/30'
        )}
      >
        {selected && (
          <div className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
        )}
      </div>

      {/* Content */}
      <div className="flex flex-col items-center gap-0.5 w-full min-w-0">
        <div className="font-semibold text-xs truncate w-full">{absenceType.name}</div>
        <div className="flex items-baseline gap-0.5">
          <span className="text-base font-bold" style={{ color }}>
            {isUnlimited ? '∞' : formatDays(item.balance.available ?? 0)}
          </span>
          <span className="text-[10px] text-muted-foreground leading-none">
            {isUnlimited ? 'unlimited' : 'days'}
          </span>
        </div>
      </div>
    </button>
  );
}
